// src/OPERATOR/OperatorHome.tsx  — React Web
// ─────────────────────────────────────────────────────────────────────────────
// Shows the current user's own driver listings (via listenToDriverData)
// Add More → /onboarding/:uid
// No operator thunks. No allDrivers thunks. No listenToAllDrivers.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useCallback, useState } from "react";
import { useNavigate }                       from "react-router-dom";
import { useSelector, useDispatch }          from "react-redux";
import {
  Car, Package, GitMerge, Calendar, Wallet,
  Radio, Plus, ChevronRight, ArrowRight,
  Settings, RefreshCw, Star, MapPin,
  CheckCircle, Clock, Eye, EyeOff,
} from "lucide-react";
import type { RootState, AppDispatch } from "../../../redux/store";
import { listenToDriverData }          from "../../../redux/driversSlice";
import Logo                      from "../../../assets/app/logo.png";

/* ─── Theme ──────────────────────────────────────────────────────────────── */
const T = {
  red: "#E50914", redDeep: "#B20710",
  gold: "#C6A75E", goldLight: "#E8C97A",
  black: "#000000", card: "#111111", surface: "#181818", border: "#242424",
  white: "#FFFFFF", smoke: "rgba(255,255,255,0.55)", dim: "rgba(255,255,255,0.28)",
  green: "#00C48C", blue: "#4A9EFF",
};

/* ─── CSS ────────────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  --red:${T.red};--red-deep:${T.redDeep};--gold:${T.gold};--gold-lt:${T.goldLight};
  --white:${T.white};--smoke:${T.smoke};--dim:${T.dim};
  --black:${T.black};--card:${T.card};--surface:${T.surface};--border:${T.border};
  --green:${T.green};--blue:${T.blue};
  --serif:'Cormorant Garamond',Georgia,serif;
  --sans:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
}

.oh-root{min-height:100vh;background:var(--black);font-family:var(--sans);color:var(--white);overflow-x:hidden;}
.oh-top-accent{height:3px;background:linear-gradient(to right,var(--gold),var(--gold-lt),var(--gold));width:100%;}

/* ── header ── */
.oh-header{position:fixed;top:3px;left:0;right:0;height:58px;background:rgba(0,0,0,.92);backdrop-filter:blur(12px);border-bottom:1px solid var(--border);display:flex;align-items:stretch;z-index:100;}
.oh-logo-block{background:var(--red);height:58px;width:72px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;padding:8px 6px 6px;cursor:pointer;margin-left:16px;}
.oh-logo-img{width:30px;height:30px;object-fit:contain;}
.oh-logo-text{font-size:6px;font-weight:800;letter-spacing:2.5px;color:var(--white);font-family:var(--serif);text-transform:uppercase;text-align:center;line-height:1;}
.oh-logo-rule{width:18px;height:1px;background:var(--white);margin-top:1px;}
.oh-nav{flex:1;display:flex;align-items:center;padding:0 20px;gap:32px;}
.oh-nav-link{font-size:11px;font-weight:500;letter-spacing:.12em;color:var(--dim);text-transform:uppercase;background:none;border:none;cursor:pointer;font-family:inherit;padding:0;transition:color .2s;}
.oh-nav-link:hover{color:var(--white);}
.oh-nav-link.active{color:var(--gold);}
.oh-refresh-btn{display:flex;align-items:center;gap:6px;background:none;border:1px solid var(--border);padding:8px 14px;color:var(--dim);font-size:11px;font-family:inherit;cursor:pointer;transition:color .2s,border-color .2s;margin-right:16px;}
.oh-refresh-btn:hover{color:var(--gold);border-color:rgba(198,167,94,.4);}
.oh-spin{animation:ohSpin .75s linear infinite;}

/* ── body ── */
.oh-body{padding:82px 20px 60px;max-width:960px;margin:0 auto;}

/* ── page header ── */
.oh-page-hdr{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:28px;opacity:0;transform:translateY(-12px);animation:ohFadeUp .5s cubic-bezier(.22,1,.36,1) .05s forwards;}
.oh-eyebrow{color:var(--gold);font-size:9px;font-weight:800;letter-spacing:4px;text-transform:uppercase;margin-bottom:8px;}
.oh-red-rule{width:22px;height:2px;background:var(--red);margin-bottom:12px;}
.oh-title{color:var(--white);font-size:clamp(26px,3vw,36px);font-weight:600;font-family:var(--serif);letter-spacing:.03em;}
.oh-live-pill{display:flex;align-items:center;gap:6px;background:rgba(0,196,140,.1);border:1px solid rgba(0,196,140,.3);padding:6px 12px;flex-shrink:0;}
.oh-live-dot{width:6px;height:6px;border-radius:50%;background:var(--green);animation:ohPulse 2s ease infinite;}
.oh-live-txt{color:var(--green);font-size:9px;font-weight:800;letter-spacing:2px;}

/* ── summary row ── */
.oh-summary{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:32px;opacity:0;transform:translateY(12px);animation:ohFadeUp .5s cubic-bezier(.22,1,.36,1) .15s forwards;}
@media(max-width:600px){.oh-summary{grid-template-columns:repeat(2,1fr);}}
.oh-sum-card{background:var(--card);border:1px solid var(--border);padding:16px 14px;display:flex;flex-direction:column;align-items:center;gap:6px;}
.oh-sum-icon{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin-bottom:2px;}
.oh-sum-val{color:var(--white);font-size:18px;font-weight:700;font-family:var(--serif);}
.oh-sum-lbl{color:var(--dim);font-size:9px;letter-spacing:.5px;}

/* ── section label ── */
.oh-section-lbl{font-size:9px;letter-spacing:.22em;color:var(--smoke);text-transform:uppercase;font-weight:700;margin-bottom:14px;}

/* ── driver listing card ── */
.oh-card-wrap{margin-bottom:18px;opacity:0;transform:translateY(16px);animation:ohFadeUp .45s cubic-bezier(.22,1,.36,1) var(--delay,.2s) forwards;}
.oh-card-border{padding:1.5px;background:linear-gradient(135deg,var(--gold),var(--gold-lt),var(--gold),#8B6914);}
.oh-card-inner{background:#0E0E0E;padding:20px;}

/* card header */
.oh-card-hdr{display:flex;align-items:center;gap:14px;margin-bottom:16px;}
.oh-card-avatar{width:48px;height:48px;border-radius:50%;flex-shrink:0;background:rgba(198,167,94,.13);border:1px solid rgba(198,167,94,.33);display:flex;align-items:center;justify-content:center;color:var(--gold);font-size:18px;font-weight:900;font-family:var(--serif);}
.oh-card-title{color:var(--white);font-size:16px;font-weight:700;margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.oh-card-meta{color:var(--dim);font-size:10px;font-weight:600;letter-spacing:.5px;}
.oh-card-div{height:1px;background:var(--border);margin-bottom:16px;}

/* published badge */
.oh-pub-badge{display:flex;align-items:center;gap:5px;flex-shrink:0;padding:4px 10px;font-size:9px;font-weight:900;letter-spacing:1px;border:1px solid;border-radius:20px;}

/* stats */
.oh-card-stats{display:flex;margin-bottom:16px;}
.oh-card-stat{flex:1;display:flex;flex-direction:column;align-items:center;}
.oh-card-stat-val{color:var(--white);font-size:15px;font-weight:800;font-family:var(--serif);margin-bottom:2px;}
.oh-card-stat-lbl{color:var(--dim);font-size:9px;letter-spacing:.3px;}
.oh-card-stat-div{width:1px;background:var(--border);}

/* caps */
.oh-caps{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:14px;}
.oh-cap-chip{display:flex;align-items:center;gap:4px;padding:4px 9px;border:1px solid;border-radius:8px;font-size:9px;font-weight:700;}
.oh-cap-on{background:rgba(229,9,20,.13);border-color:rgba(229,9,20,.26);color:var(--white);}
.oh-cap-off{background:var(--surface);border-color:var(--border);color:var(--dim);}

/* rate chip */
.oh-rate-row{display:flex;align-items:center;gap:8px;margin-bottom:14px;padding:8px 10px;background:rgba(198,167,94,.05);border:1px solid rgba(198,167,94,.14);}
.oh-rate-val{color:var(--gold);font-family:var(--serif);font-size:18px;font-weight:600;}
.oh-rate-type{color:var(--dim);font-size:10px;font-weight:700;letter-spacing:.5px;}
.oh-rate-disc{background:var(--red);color:var(--white);font-size:9px;font-weight:800;padding:2px 8px;margin-left:auto;}

/* location */
.oh-loc-row{display:flex;align-items:center;gap:6px;margin-bottom:14px;}
.oh-loc-txt{color:var(--smoke);font-size:11px;}

/* actions */
.oh-card-actions{display:flex;gap:10px;}
.oh-edit-btn{display:flex;align-items:center;gap:6px;border:1px solid rgba(198,167,94,.33);padding:11px 14px;background:rgba(198,167,94,.06);cursor:pointer;color:var(--gold);font-size:12px;font-weight:700;font-family:inherit;transition:background .2s,border-color .2s;}
.oh-edit-btn:hover{background:rgba(198,167,94,.12);border-color:rgba(198,167,94,.55);}
.oh-open-btn{flex:1;display:flex;align-items:center;justify-content:center;gap:8px;padding:12px 0;background:linear-gradient(to right,var(--red),var(--red-deep));border:none;color:var(--white);font-size:12px;font-weight:800;letter-spacing:.5px;cursor:pointer;font-family:inherit;transition:opacity .2s;}
.oh-open-btn:hover{opacity:.88;}

/* ── add more ── */
.oh-add-more{border:1.5px dashed rgba(198,167,94,.22);background:transparent;cursor:pointer;width:100%;display:flex;align-items:center;gap:14px;padding:18px;margin-bottom:8px;font-family:inherit;transition:border-color .2s;}
.oh-add-more:hover{border-color:rgba(198,167,94,.5);}
.oh-add-icon{width:44px;height:44px;border-radius:50%;flex-shrink:0;background:rgba(198,167,94,.1);border:1px solid rgba(198,167,94,.22);display:flex;align-items:center;justify-content:center;color:var(--gold);}
.oh-add-title{color:var(--white);font-size:14px;font-weight:700;margin-bottom:2px;text-align:left;}
.oh-add-sub{color:var(--dim);font-size:11px;text-align:left;}

/* ── empty ── */
.oh-empty{display:flex;flex-direction:column;align-items:center;padding:60px 32px;text-align:center;opacity:0;animation:ohFadeUp .5s ease .2s forwards;}
.oh-empty-icon{width:100px;height:100px;border-radius:50%;background:rgba(198,167,94,.1);border:1px solid rgba(198,167,94,.26);display:flex;align-items:center;justify-content:center;margin-bottom:28px;color:var(--gold);}
.oh-empty-title{color:var(--white);font-size:28px;font-weight:700;font-family:var(--serif);margin-bottom:12px;}
.oh-empty-sub{color:var(--smoke);font-size:14px;line-height:1.6;margin-bottom:36px;}
.oh-empty-cta{display:flex;align-items:center;gap:8px;padding:16px 32px;background:linear-gradient(to right,var(--red),var(--red-deep));border:none;color:var(--white);font-size:12px;font-weight:800;letter-spacing:3px;text-transform:uppercase;cursor:pointer;font-family:inherit;transition:opacity .2s;}
.oh-empty-cta:hover{opacity:.88;}

/* ── loader ── */
.oh-loader{position:fixed;inset:0;background:var(--black);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;z-index:9999;}
.oh-spinner{width:28px;height:28px;border-radius:50%;border:2px solid rgba(198,167,94,.2);border-top-color:var(--red);animation:ohSpin .75s linear infinite;}
.oh-loader-txt{color:var(--smoke);font-size:13px;letter-spacing:1px;}

/* ── footer ── */
.oh-footer{background:var(--black);border-top:1px solid rgba(255,255,255,.08);padding:48px 24px 0;margin-top:40px;}
.oh-footer-inner{max-width:960px;margin:0 auto;}
.oh-footer-copy{padding:20px 0;font-size:11px;color:rgba(255,255,255,.22);border-top:1px solid rgba(255,255,255,.07);}
.oh-footer-bottom{overflow:hidden;display:flex;align-items:flex-end;border-top:1px solid rgba(255,255,255,.06);}
.oh-footer-wordmark{flex:1;font-size:min(18vw,220px);font-weight:300;color:var(--white);font-family:var(--serif);letter-spacing:.15em;line-height:1;padding:28px 0 20px;white-space:nowrap;}
.oh-footer-logo{flex-shrink:0;background:var(--red);width:80px;height:88px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;padding:10px 8px 8px;margin-right:16px;margin-bottom:20px;align-self:flex-end;}
.oh-footer-logo-img{width:38px;height:38px;object-fit:contain;}
.oh-footer-logo-rule{width:22px;height:1px;background:white;margin-top:2px;}
.oh-footer-logo-txt{font-size:6.5px;font-weight:800;letter-spacing:3px;color:white;font-family:var(--serif);text-transform:uppercase;text-align:center;}

@keyframes ohFadeUp{to{opacity:1;transform:translateY(0);}}
@keyframes ohSpin{to{transform:rotate(360deg);}}
@keyframes ohPulse{0%,100%{opacity:1}50%{opacity:.3}}
::-webkit-scrollbar{width:0;height:0;}
`;

/* ─── Rate type label ────────────────────────────────────────────────────── */
const RATE_LABELS: Record<string, string> = {
  perKm: "/km", perPerson: "/person", perDay: "/day",
  perCar: "flat", perSeat: "/seat", perPersonPerKm: "/p·km",
};

/* ─── Capability chip ────────────────────────────────────────────────────── */
function CapChip({ label, active, Icon: Ic }: { label: string; active: boolean; Icon: React.ElementType }) {
  return (
    <span className={`oh-cap-chip ${active ? "oh-cap-on" : "oh-cap-off"}`}>
      <Ic size={10} />{label}
    </span>
  );
}

/* ─── Discount pct from discountApplied obj ──────────────────────────────── */
function discPct(d?: Record<string, boolean>): number {
  if (!d) return 0;
  return d.is80 ? 80 : d.is50 ? 50 : d.is25 ? 25 : d.is10 ? 10 : d.is5 ? 5 : 0;
}

/* ─── Driver Listing Card ────────────────────────────────────────────────── */
function DriverCard({
  listing,
  index,
  onEdit,
  onOpen,
}: {
  listing: any;
  index:   number;
  onEdit:  (id: string) => void;
  onOpen:  (l: any) => void;
}) {
  const isPublished = listing.isPublished && !listing.isDraft;
  const pubColor    = isPublished ? T.green : T.gold;
  const brandModel  = [listing.brandName || listing.brand, listing.model].filter(Boolean).join(" ") || "Vehicle";
  const initials    = brandModel.slice(0, 2).toUpperCase();
  const disc        = discPct(listing.discountApplied);
  const extras      = listing.extras ? Object.keys(listing.extras).length : 0;

  return (
    <div className="oh-card-wrap" style={{ "--delay": `${0.2 + index * 0.08}s` } as React.CSSProperties}>
      <div className="oh-card-border">
        <div className="oh-card-inner">

          {/* Header */}
          <div className="oh-card-hdr">
            <div className="oh-card-avatar">{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="oh-card-title">{brandModel}</div>
              <div className="oh-card-meta">
                {listing.vehicleSize ? `${listing.vehicleSize} · ` : ""}
                {listing.licensePlate || "—"}
                {listing.year ? ` · ${listing.year}` : ""}
              </div>
            </div>
            {/* Published badge */}
            <div
              className="oh-pub-badge"
              style={{ color: pubColor, borderColor: pubColor + "55", background: pubColor + "14" }}
            >
              {isPublished ? <CheckCircle size={9} /> : <Clock size={9} />}
              {isPublished ? "LIVE" : listing.inReview ? "IN REVIEW" : "DRAFT"}
            </div>
          </div>

          <div className="oh-card-div" />

          {/* Stats */}
          <div className="oh-card-stats">
            {[
              { val: String(listing.capacity || listing.seats || "—"), lbl: "Seats"   },
              { val: listing.fuelTypeName  || listing.fuelType  || "—", lbl: "Fuel"   },
              { val: listing.transmissionName || listing.transmission || "—", lbl: "Gearbox" },
              { val: listing.rating ? `${Number(listing.rating).toFixed(1)}` : "New", lbl: "Rating", color: T.gold },
            ].map((s, i) => (
              <>
                {i > 0 && <div key={`d${i}`} className="oh-card-stat-div" />}
                <div key={s.lbl} className="oh-card-stat">
                  <div className="oh-card-stat-val" style={s.color ? { color: s.color } : undefined}>{s.val}</div>
                  <div className="oh-card-stat-lbl">{s.lbl}</div>
                </div>
              </>
            ))}
          </div>

          {/* Rate */}
          <div className="oh-rate-row">
            <Radio size={12} color={T.gold} />
            <span className="oh-rate-val">€{listing.rate ?? "—"}</span>
            <span className="oh-rate-type">{RATE_LABELS[listing.rateType] || listing.rateType || "/trip"}</span>
            {disc > 0 && <span className="oh-rate-disc">-{disc}%</span>}
            {extras > 0 && (
              <span style={{ marginLeft: disc > 0 ? 6 : "auto", color: T.dim, fontSize: 10 }}>
                +{extras} extra{extras !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Capabilities */}
          <div className="oh-caps">
            <CapChip label="Sharing"   Icon={GitMerge} active={listing.rideSharing      ?? false} />
            <CapChip label="Parcels"   Icon={Package}  active={listing.allowParcel       ?? false} />
            <CapChip label="Scheduled" Icon={Calendar} active={listing.allowScheduled    ?? false} />
          </div>

          {/* Location */}
          {(listing.city || listing.country) && (
            <div className="oh-loc-row">
              <MapPin size={11} color={T.dim} />
              <span className="oh-loc-txt">
                {[listing.city, listing.country].filter(Boolean).join(", ")}
                {listing.serviceRadiusKm ? ` · ${listing.serviceRadiusKm} km radius` : ""}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="oh-card-actions">
            <button className="oh-edit-btn" onClick={() => onEdit(listing.listingId || listing.id)}>
              <Settings size={14} />
              Edit
            </button>
            <button className="oh-open-btn" onClick={() => onOpen(listing)}>
              {isPublished ? <><Eye size={13} /> View Listing</> : <><ArrowRight size={13} /> Complete Profile</>}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* MAIN                                                                         */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function OperatorHome() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((s: RootState) => s.auth);

  // ── Only driversSlice — listenToDriverData ──────────────────────────────
  const {
    listings = [],
    loading   = false,
  } = useSelector((s: RootState) => s.drivers ?? {});

  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  /* inject CSS once */
  useEffect(() => {
    const id = "oh-css";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style"); tag.id = id; tag.textContent = CSS;
      document.head.appendChild(tag);
    }
  }, []);

  /* start real-time listener for current user's driver listings */
  useEffect(() => {
    const uid = (user as any)?.userId || (user as any)?.uid;
    if (!uid) return;

    // listenToDriverData returns an unsubscribe fn
    const result = dispatch(listenToDriverData(uid) as any);
    // handle both: thunk returns a promise that resolves to unsubscribe, or returns unsubscribe directly
    let unsub: (() => void) | null = null;
    if (typeof result === "function") {
      unsub = result;
    } else if (result && typeof result.then === "function") {
      result.then((r: any) => { if (typeof r === "function") unsub = r; });
    }
    return () => { if (unsub) unsub(); };
  }, [user, refreshKey, dispatch]);

  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
    setTimeout(() => setRefreshing(false), 900);
  };

  const uid = (user as any)?.userId || (user as any)?.uid;

  const handleEdit = (listingId: string) =>
    navigate(`/driveredit/${listingId}`);

  const handleOpen = (listing: any) =>
    navigate(`/driversdashboard`, { state: { listingId: listing.listingId || listing.id } });

  const handleAddMore = () =>
    navigate(`/onboarding/${uid}`);

  /* derived stats */
  const published  = listings.filter((l: any) => l?.isPublished && !l?.isDraft).length;
  const inReview   = listings.filter((l: any) => l?.inReview && !l?.isPublished).length;
  const avgRating  = listings.length
    ? (listings.reduce((s: number, l: any) => s + (Number(l?.rating) || 0), 0) / listings.length).toFixed(1)
    : "—";

  const pageTitle = listings.length === 0 ? "Get Started"
    : listings.length === 1 ? (listings[0]?.model || "Your Vehicle")
    : `${listings.length} Listings`;

  /* loading */
  if (loading && !listings.length) {
    return (
      <>
        <style>{CSS}</style>
        <div className="oh-loader">
          <div className="oh-spinner" />
          <p className="oh-loader-txt">Loading your listings…</p>
        </div>
      </>
    );
  }

  return (
    <div className="oh-root">
      <style>{CSS}</style>

      <div className="oh-top-accent" />

      {/* ── Header ── */}
      <header className="oh-header">
        <div className="oh-logo-block" onClick={() => navigate("/")}>
          <img src={Logo} alt="Xafari" className="oh-logo-img" />
          <div className="oh-logo-rule" />
          <div className="oh-logo-text">XAFARI</div>
        </div>
        <nav className="oh-nav">
          <button className="oh-nav-link active">My Vehicles</button>
          <button className="oh-nav-link" onClick={() => navigate("/")}>Home</button>
          {listings.length > 0 && (
            <button className="oh-nav-link" onClick={() => navigate(`/driversdashboard/${uid}`)}>
              Dashboard
            </button>
          )}
        </nav>
        <button className="oh-refresh-btn" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw size={13} className={refreshing ? "oh-spin" : ""} />
          Refresh
        </button>
      </header>

      <div className="oh-body">

        {/* ── Page header ── */}
        <div className="oh-page-hdr">
          <div>
            <p className="oh-eyebrow">Driver Portal</p>
            <div className="oh-red-rule" />
            <h1 className="oh-title">{pageTitle}</h1>
          </div>
          {published > 0 && (
            <div className="oh-live-pill">
              <div className="oh-live-dot" />
              <span className="oh-live-txt">{published} Live</span>
            </div>
          )}
        </div>

        {/* ── Summary row (only when listings exist) ── */}
        {listings.length > 0 && (
          <div className="oh-summary">
            {[
              { lbl: "Listings", val: String(listings.length), color: T.gold,  bg: T.gold  + "18", Icon: Car       },
              { lbl: "Live",     val: String(published),       color: T.green, bg: T.green + "18", Icon: CheckCircle },
              { lbl: "In Review",val: String(inReview),        color: T.blue,  bg: T.blue  + "18", Icon: Clock     },
              { lbl: "Avg Rating",val: avgRating,              color: T.gold,  bg: T.gold  + "18", Icon: Star      },
            ].map(({ lbl, val, color, bg, Icon: Ic }) => (
              <div key={lbl} className="oh-sum-card">
                <div className="oh-sum-icon" style={{ background: bg }}><Ic size={16} color={color} /></div>
                <div className="oh-sum-val" style={{ color }}>{val}</div>
                <div className="oh-sum-lbl">{lbl}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && listings.length === 0 && (
          <div className="oh-empty">
            <div className="oh-empty-icon"><Car size={52} /></div>
            <h2 className="oh-empty-title">Register Your Vehicle</h2>
            <p className="oh-empty-sub">
              List your car and start accepting rides.<br />
              Set your own rates, schedule, and service area.
            </p>
            <button className="oh-empty-cta" onClick={handleAddMore}>
              <Plus size={16} />
              Register Now
            </button>
          </div>
        )}

        {/* ── Listings ── */}
        {listings.length > 0 && (
          <>
            <p className="oh-section-lbl">
              {listings.length === 1 ? "Your Vehicle" : "Your Vehicles"}
            </p>

            {listings.map((listing: any, i: number) => (
              <DriverCard
                key={listing.listingId || listing.id || i}
                listing={listing}
                index={i}
                onEdit={handleEdit}
                onOpen={handleOpen}
              />
            ))}

            {/* Add more */}
            <button className="oh-add-more" onClick={handleAddMore}>
              <div className="oh-add-icon"><Plus size={22} /></div>
              <div style={{ flex: 1 }}>
                <div className="oh-add-title">Add Another Vehicle</div>
                <div className="oh-add-sub">Register a new listing and set its own pricing</div>
              </div>
              <ChevronRight size={18} color={T.dim} />
            </button>
          </>
        )}
      </div>

      {/* ── Footer ── */}
      <footer className="oh-footer">
        <div className="oh-footer-inner">
          <div className="oh-footer-copy">
            © 2025 – {new Date().getFullYear()} Xafari Technologies — All rights reserved.
          </div>
        </div>
        <div className="oh-footer-bottom">
          <span className="oh-footer-wordmark">XAFARI</span>
          <div className="oh-footer-logo">
            <img src={Logo} alt="Xafari" className="oh-footer-logo-img" />
            <div className="oh-footer-logo-rule" />
            <div className="oh-footer-logo-txt">XAFARI</div>
          </div>
        </div>
      </footer>
    </div>
  );
}